import { isPraise } from '@/lib/who-match';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { PrayerAttemptV1 } from '@/types/prayer-command';
import type { Prayer } from '@/types/prayer';
import type { TestimonyDraftV1 } from '@/types/testimony-draft';
import { discardTestimonyDraft, listPrayerAttempts, listTestimonyDrafts } from '@/lib/content-lifecycle/store';
import { getCurrentContentLifecycleContext } from '@/lib/content-lifecycle/sync';
import { testimonyDraftLinkedTo, testimonyDraftSameVersion } from '@/lib/testimony-draft';
import { getContentOwnerBinding } from '@/lib/content-owner';
import { getAuthEpoch, getAuthUserId, waitForAuthResolution } from '@/lib/keepsake/epochs';
import { isCoreReadOnly } from '@/lib/core-cutover';
import { getSupabase } from '@/lib/supabase';
import { useAdiaStore } from '@/lib/store';
import { isSelf, whoOf } from '@/lib/prayer-utils';
import { setNotesListShown } from '@/lib/sign-out-causes';
import {
  cancelTestimonyOpenRequest,
  requestTestimonyOpen,
  takeTestimonyReturnFocus,
} from '@/lib/testimony-open-request';
import styles from './TestimonyDraftRecovery.module.css';

/**
 * "Notes you started" (testimony plan rev 4 sections 5.1 and 9.1; Step 3
 * proposal section 3). Lists this owner's testimony drafts in Settings, above
 * the account section. Open, Copy and Discard; Discard is O5 and is offered
 * only when no unfinished action is LINKED. Every draft is listed, linked ones
 * too, even when the unfinished-changes card may show the same words (rev 4
 * section 23.2). Nothing is cleaned up on its own.
 *
 * Authority: the list belongs to the owner and auth epoch it captured, and it
 * shows rows only while that owner is signed in AND is the durable owner of
 * this profile. A signed-out or sessionless profile shows nothing. Every write
 * (O5) is re-checked inside its own transaction against the bytes the user
 * confirmed; Open writes nothing.
 */

type RowState = 'unlinked' | 'undetermined' | 'linked' | 'linked-same-save';
type Row = { raw: string; draft: TestimonyDraftV1; state: RowState };
type Auth = { ownerId: string; authEpoch: number };
type RowNotice = 'copied' | 'copyFailed' | 'changedElsewhere' | 'discardFailed';
type Confirm = { prayerId: string; raw: string; draft: TestimonyDraftV1; state: RowState };

const POLL_MS = 2000;
const FIRST_WORDS = 80;

function rowState(draft: TestimonyDraftV1, attempts: PrayerAttemptV1[]): RowState {
  if (!draft.link) return 'unlinked';
  const linked = attempts.find(
    (attempt) => attempt.prayerId === draft.prayerId && testimonyDraftLinkedTo(draft, attempt),
  );
  if (!linked) return 'undetermined';
  return draft.link.purpose === 'save' && testimonyDraftSameVersion(draft) ? 'linked-same-save' : 'linked';
}

function firstWords(text: string): string {
  const trimmed = text.trim().replace(/\s+/g, ' ');
  if (trimmed.length <= FIRST_WORDS) return trimmed;
  const cut = trimmed.slice(0, FIRST_WORDS);
  const space = cut.lastIndexOf(' ');
  return `${(space > 40 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

function authIsCurrent(auth: Auth | null): auth is Auth {
  return Boolean(auth?.ownerId) && getAuthUserId() === auth?.ownerId && getAuthEpoch() === auth?.authEpoch;
}

export default function TestimonyDraftRecovery() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const prayers = useAdiaStore((state) => state.prayers);
  const prayersReady = useAdiaStore((state) => state.hydration.state === 'ready');
  const readOnly = isCoreReadOnly();

  const authRef = useRef<Auth | null>(null);
  const [rows, setRows] = useState<Row[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [busy, setBusy] = useState(false);
  const latch = useRef(false);
  const [confirm, setConfirm] = useState<Confirm | null>(null);
  const confirmRef = useRef<Confirm | null>(null);
  confirmRef.current = confirm;
  const [notices, setNotices] = useState<Record<string, RowNotice>>({});
  // The polite status lives outside the list and stays mounted while this
  // owner's session is current, so an outcome is still announced after the
  // last row goes. It is cleared on any account or session change.
  const [status, setStatus] = useState('');
  const headingRef = useRef<HTMLHeadingElement>(null);
  const actionRefs = useRef(new Map<string, HTMLButtonElement>());
  // Where focus goes after the next render, decided only while the element
  // being removed (an open confirmation) actually held focus. `list` means
  // the first remaining row action, then the headings; `heading` (return
  // from an Open whose row is gone) skips the rows.
  const focusAfterLoad = useRef<{ kind: 'row'; prayerId: string } | { kind: 'list' } | { kind: 'heading' } | null>(null);
  const returnFocusDraft = useRef<string | null>(null);
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const frames = useRef(new Set<number>());
  const tRef = useRef(t);
  tRef.current = t;

  // Single-flight reads: never two at once, and a result is published only
  // when it is the newest read and its owner and epoch are still current.
  const loading = useRef(false);
  const loadAgain = useRef(false);
  const loadSeq = useRef(0);
  const disposed = useRef(false);

  const cancelPendingWork = useCallback(() => {
    for (const frame of frames.current) cancelAnimationFrame(frame);
    frames.current.clear();
    clearTimeout(copiedTimer.current);
    focusAfterLoad.current = null;
    returnFocusDraft.current = null;
  }, []);

  const clearShown = useCallback(() => {
    cancelPendingWork();
    setRows(null);
    setConfirm(null);
    setNotices({});
    setLoadFailed(false);
    setStatus('');
  }, [cancelPendingWork]);

  const load = useCallback(async (): Promise<void> => {
    if (loading.current) {
      loadAgain.current = true;
      return;
    }
    loading.current = true;
    try {
      do {
        loadAgain.current = false;
        const auth = authRef.current;
        if (!authIsCurrent(auth)) {
          if (!disposed.current) clearShown();
          return;
        }
        const seq = ++loadSeq.current;
        try {
          const binding = await getContentOwnerBinding();
          if (binding !== auth.ownerId) {
            if (!disposed.current && seq === loadSeq.current) clearShown();
            continue;
          }
          const [drafts, attempts] = await Promise.all([
            listTestimonyDrafts(auth.ownerId),
            listPrayerAttempts(auth.ownerId),
          ]);
          const finalBinding = await getContentOwnerBinding();
          if (disposed.current || seq !== loadSeq.current || !authIsCurrent(auth) || finalBinding !== auth.ownerId) {
            if (!disposed.current && !authIsCurrent(auth)) clearShown();
            continue;
          }
          const next = drafts
            .filter((slot) => slot.draft.ownerId === auth.ownerId)
            .map((slot) => ({ raw: slot.raw, draft: slot.draft, state: rowState(slot.draft, attempts) }));
          setRows((current) => (
            current !== null && current.length === next.length &&
            current.every((row, index) => row.raw === next[index].raw && row.state === next[index].state)
              ? current
              : next
          ));
          setLoadFailed(false);
          // A confirmation stays bound to the bytes the user confirmed. If the
          // note or its link changed meanwhile, end the confirmation and say so
          // rather than putting other words under the same button: on the row
          // when it is still listed, otherwise through the status, because a
          // removed row can carry no message. Focus moves only when it was
          // inside the confirmation being removed.
          const open = confirmRef.current;
          if (open) {
            const current = next.find((row) => row.draft.prayerId === open.prayerId);
            if (!current || current.raw !== open.raw || current.state !== open.state) {
              const hadFocus = confirmHasFocus(open.prayerId);
              const stillListed = current !== undefined;
              setConfirm(null);
              if (stillListed) {
                setNotices((all) => ({ ...all, [open.prayerId]: 'changedElsewhere' }));
              } else {
                setStatus(tRef.current('testimonyDraft.list.changedElsewhereGone'));
              }
              if (hadFocus) {
                focusAfterLoad.current = stillListed ? { kind: 'row', prayerId: open.prayerId } : { kind: 'list' };
              }
            }
          }
        } catch {
          if (!disposed.current && seq === loadSeq.current && authIsCurrent(auth)) setLoadFailed(true);
        }
      } while (loadAgain.current && !disposed.current);
    } finally {
      loading.current = false;
    }
  }, [clearShown]);

  useEffect(() => {
    let active = true;
    disposed.current = false;
    // A Settings visit ends any Open still waiting from an earlier tap.
    cancelTestimonyOpenRequest();
    const subscription = getSupabase()?.auth.onAuthStateChange(() => {
      queueMicrotask(() => {
        if (!disposed.current && !authIsCurrent(authRef.current)) {
          clearShown();
          setNotesListShown(null);
        }
      });
    }).data.subscription;
    void waitForAuthResolution().then(() => {
      if (!active) return;
      const ownerId = getAuthUserId();
      // No session, no list: ownership is never inferred without one.
      if (!ownerId) return;
      if (!authRef.current) {
        authRef.current = { ownerId, authEpoch: getAuthEpoch() };
        returnFocusDraft.current = takeTestimonyReturnFocus(ownerId, authRef.current.authEpoch);
      }
      void load();
    }).catch(() => {});
    const refresh = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      void load();
    };
    const interval = setInterval(refresh, POLL_MS);
    document.addEventListener('visibilitychange', refresh);
    window.addEventListener('focus', refresh);
    return () => {
      active = false;
      disposed.current = true;
      clearInterval(interval);
      cancelPendingWork();
      document.removeEventListener('visibilitychange', refresh);
      window.removeEventListener('focus', refresh);
      subscription?.unsubscribe();
      setNotesListShown(null);
    };
  }, [load, clearShown, cancelPendingWork]);

  // Every draft, in a stable order. Linked drafts stay listed (rev 4 section
  // 23.2): the unfinished-changes card reads only when Settings opens, so a
  // draft another tab links later would otherwise have no visible copy.
  const prayerById = new Map<string, Prayer>(prayers.map((prayer) => [prayer.id, prayer]));
  const visible = [...(rows ?? [])]
    .sort((a, b) => {
      const pa = prayersReady ? prayerById.get(a.draft.prayerId) : undefined;
      const pb = prayersReady ? prayerById.get(b.draft.prayerId) : undefined;
      if (pa && !pb) return -1;
      if (!pa && pb) return 1;
      if (pa && pb) {
        const byDate = new Date(pb.createdAt).getTime() - new Date(pa.createdAt).getTime();
        if (byDate !== 0) return byDate;
      }
      return a.draft.prayerId < b.draft.prayerId ? -1 : a.draft.prayerId > b.draft.prayerId ? 1 : 0;
    });
  const shown = authIsCurrent(authRef.current) && (visible.length > 0 || loadFailed);
  const visibleOrder = useRef<string[]>([]);
  visibleOrder.current = visible.map((row) => row.draft.prayerId);

  useEffect(() => {
    const auth = authRef.current;
    setNotesListShown(shown && auth && visible.length > 0 ? auth : null);
  }, [shown, visible.length]);

  // Focus after a Discard, and when Settings returns from an Open.
  useEffect(() => {
    if (rows === null) return;
    const returning = returnFocusDraft.current;
    if (returning !== null) {
      returnFocusDraft.current = null;
      const row = visible.find((item) => item.draft.draftId === returning);
      focusAfterLoad.current = row ? { kind: 'row', prayerId: row.draft.prayerId } : { kind: 'heading' };
    }
    const target = focusAfterLoad.current;
    if (!target) return;
    focusAfterLoad.current = null;
    const auth = authRef.current;
    const frame = requestAnimationFrame(() => {
      frames.current.delete(frame);
      if (disposed.current || !authIsCurrent(auth) || authRef.current !== auth) return;
      if (target.kind === 'row') {
        const button = actionRefs.current.get(target.prayerId);
        if (button) {
          button.focus();
          return;
        }
      }
      const survivor = target.kind === 'list'
        ? visibleOrder.current.map((id) => actionRefs.current.get(id)).find((button) => button !== undefined)
        : undefined;
      if (survivor) {
        survivor.focus();
        return;
      }
      if (headingRef.current) {
        headingRef.current.focus();
        return;
      }
      focusAccountHeading();
    });
    frames.current.add(frame);
  });

  const nextFrame = (work: () => void) => {
    const frame = requestAnimationFrame(() => {
      frames.current.delete(frame);
      if (!disposed.current) work();
    });
    frames.current.add(frame);
  };

  if (!authIsCurrent(authRef.current)) return null;

  // Stays mounted for this owner and session even when the list is hidden.
  const statusRegion = (
    <p
      id="testimony-draft-list-status"
      className={shown ? styles.status : styles.visuallyHidden}
      role="status"
      aria-live="polite"
    >
      {status}
    </p>
  );

  if (!shown) return <>{null}{statusRegion}</>;

  const setRowNotice = (prayerId: string, notice: RowNotice | null) => {
    setNotices((all) => {
      const next = { ...all };
      if (notice) next[prayerId] = notice; else delete next[prayerId];
      return next;
    });
  };

  const copy = async (row: Row) => {
    const prayerId = row.draft.prayerId;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('clipboard-unavailable');
      await navigator.clipboard.writeText(row.draft.text);
      setRowNotice(prayerId, 'copied');
      setStatus(t('testimonyDraft.list.copied'));
      clearTimeout(copiedTimer.current);
      copiedTimer.current = setTimeout(() => {
        setNotices((all) => {
          if (all[prayerId] !== 'copied') return all;
          const next = { ...all };
          delete next[prayerId];
          return next;
        });
        setStatus('');
      }, 2000);
    } catch {
      setRowNotice(prayerId, 'copyFailed');
      setStatus('');
    }
  };

  const open = (row: Row) => {
    const auth = authRef.current;
    if (latch.current || readOnly || !authIsCurrent(auth)) return;
    if (!prayersReady || !prayerById.has(row.draft.prayerId)) return;
    latch.current = true;
    requestTestimonyOpen({
      ownerId: auth.ownerId,
      authEpoch: auth.authEpoch,
      prayerId: row.draft.prayerId,
      draftId: row.draft.draftId,
    });
    navigate('/');
  };

  const startDiscard = (row: Row) => {
    if (busy || readOnly) return;
    setRowNotice(row.draft.prayerId, null);
    setConfirm({ prayerId: row.draft.prayerId, raw: row.raw, draft: row.draft, state: row.state });
    focusAfterLoad.current = null;
    nextFrame(() => document.getElementById(confirmKeepId(row.draft.prayerId))?.focus());
  };

  const cancelDiscard = (prayerId: string) => {
    setConfirm(null);
    nextFrame(() => actionRefs.current.get(prayerId)?.focus());
  };

  const discard = async (target: Confirm) => {
    const auth = authRef.current;
    if (latch.current || readOnly || !authIsCurrent(auth)) return;
    latch.current = true;
    setBusy(true);
    const index = visible.findIndex((row) => row.draft.prayerId === target.prayerId);
    const after = visible[index + 1] ?? visible[index - 1] ?? null;
    try {
      const context = await getCurrentContentLifecycleContext(auth);
      const result = await discardTestimonyDraft(context, { prayerId: target.prayerId, shownRaw: target.raw });
      if (disposed.current || !authIsCurrent(auth)) return;
      const hadFocus = confirmHasFocus(target.prayerId);
      setConfirm(null);
      setStatus(result === 'discarded' ? t('testimonyDraft.list.discarded') : t('testimonyDraft.list.alreadyRemoved'));
      setRows((current) => (current ?? []).filter((row) => row.draft.prayerId !== target.prayerId));
      if (hadFocus) focusAfterLoad.current = after ? { kind: 'row', prayerId: after.draft.prayerId } : { kind: 'list' };
    } catch (error) {
      if (disposed.current || !authIsCurrent(auth)) return;
      const hadFocus = confirmHasFocus(target.prayerId);
      setConfirm(null);
      const code = error instanceof Error ? error.message : '';
      const changed = code === 'testimony-draft-linked' || code === 'testimony-draft-stale' ||
        code === 'testimony-draft-foreign';
      setRowNotice(target.prayerId, changed ? 'changedElsewhere' : 'discardFailed');
      if (hadFocus) focusAfterLoad.current = { kind: 'row', prayerId: target.prayerId };
    } finally {
      latch.current = false;
      if (!disposed.current) setBusy(false);
      void load();
    }
  };

  return (
    <>
    <section className={styles.section} aria-labelledby="testimony-draft-list-title">
      <h2
        id="testimony-draft-list-title"
        ref={headingRef}
        tabIndex={-1}
        className={styles.title}
      >
        {t('testimonyDraft.list.title')}
      </h2>
      <p className={styles.intro}>
        {readOnly ? t('testimonyDraft.list.readOnly') : t('testimonyDraft.list.intro')}
      </p>
      {loadFailed && (
        <div className={styles.loadFailed}>
          <p role="alert">{t('testimonyDraft.list.loadFailed')}</p>
          <button type="button" className={styles.action} onClick={() => { void load(); }}>
            {t('testimonyDraft.tryAgain')}
          </button>
        </div>
      )}
      <ul className={styles.list}>
        {visible.map((row) => {
          const prayerId = row.draft.prayerId;
          const confirming = confirm?.prayerId === prayerId ? confirm : null;
          // While a confirmation is open the row shows exactly what was confirmed.
          const draft = confirming ? confirming.draft : row.draft;
          const state = confirming ? confirming.state : row.state;
          const prayer = prayersReady ? prayerById.get(prayerId) : undefined;
          const unavailable = prayersReady && !prayer;
          const rawWho = prayer ? whoOf(prayer) : '';
          const who = prayer ? (isPraise(prayer) ? t('entry.praiseLabel') : isSelf(rawWho) ? t('people.you') : rawWho) : '';
          // A prayer for the person themself gets its own Open name ("Open your note").
          const openAria = prayer && isPraise(prayer)
            ? t('entry.openPraiseNote')
            : prayer && isSelf(rawWho)
            ? t('testimonyDraft.list.openAriaSelf')
            : t('testimonyDraft.list.openAria', { who });
          const words = prayer?.rawText ? firstWords(prayer.rawText) : '';
          const linked = state === 'linked' || state === 'linked-same-save';
          const canDiscard = !readOnly && !linked;
          const canOpen = !readOnly && Boolean(prayer);
          const changedSince = state === 'unlinked' && prayer !== undefined &&
            (prayer.testimony ?? null) !== draft.baseTestimony;
          const notice = notices[prayerId];
          const firstAction = canOpen ? 'open' : draft.text ? 'copy' : canDiscard ? 'discard' : null;
          const bindFirst = (name: 'open' | 'copy' | 'discard') => (element: HTMLButtonElement | null) => {
            if (name !== firstAction) return;
            if (element) actionRefs.current.set(prayerId, element);
            else actionRefs.current.delete(prayerId);
          };
          return (
            <li key={prayerId} className={styles.card}>
              {prayer ? (
                <>
                  <h3 className={styles.who}>{who}</h3>
                  {words && <p className={styles.prayerWords}>{words}</p>}
                </>
              ) : unavailable ? (
                <p className={styles.line}>{t('testimonyDraft.list.unavailable')}</p>
              ) : null}
              <p className={styles.noteLabel}>{t('testimonyDraft.list.noteLabel')}</p>
              {draft.text ? (
                <p className={styles.note}>{draft.text}</p>
              ) : (
                <p className={styles.line}>{t('testimonyDraft.list.clearsNote')}</p>
              )}
              {changedSince && <p className={styles.line}>{t('testimonyDraft.changedSince')}</p>}
              {linked && <p className={styles.line}>{t('testimonyDraft.list.linked')}</p>}
              {notice === 'changedElsewhere' && (
                <p className={styles.alert} role="alert">{t('testimonyDraft.list.changedElsewhere')}</p>
              )}
              {notice === 'discardFailed' && (
                <p className={styles.alert} role="alert">{t('testimonyDraft.list.discardFailed')}</p>
              )}
              {notice === 'copyFailed' && (
                <p className={styles.alert} role="alert">{t('testimonyDraft.list.copyFailed')}</p>
              )}
              {confirming ? (
                <div className={styles.confirm} id={confirmId(prayerId)}>
                  <p className={styles.line}>{t('testimonyDraft.discardConfirm')}</p>
                  <div className={styles.actions}>
                    <button
                      type="button"
                      id={confirmKeepId(prayerId)}
                      className={styles.action}
                      aria-disabled={busy || undefined}
                      onClick={() => { if (!busy) cancelDiscard(prayerId); }}
                    >
                      {t('testimonyDraft.keep')}
                    </button>
                    <button
                      type="button"
                      className={`${styles.action} ${styles.danger}`}
                      aria-disabled={busy || undefined}
                      onClick={() => { void discard(confirming); }}
                    >
                      {t('testimonyDraft.discardDraft')}
                    </button>
                  </div>
                </div>
              ) : (
                <div className={styles.actions}>
                  {canOpen && (
                    <button
                      type="button"
                      ref={bindFirst('open')}
                      className={styles.action}
                      aria-label={openAria}
                      aria-disabled={busy || undefined}
                      onClick={() => { if (!busy) open(row); }}
                    >
                      {t('testimonyDraft.list.open')}
                    </button>
                  )}
                  {draft.text && (
                    <button
                      type="button"
                      ref={bindFirst('copy')}
                      className={styles.action}
                      aria-label={notice === 'copied' ? undefined : t('testimonyDraft.copyAria')}
                      onClick={() => { void copy(row); }}
                    >
                      {notice === 'copied' ? t('testimonyDraft.list.copied') : t('testimonyDraft.list.copy')}
                    </button>
                  )}
                  {canDiscard && (
                    <button
                      type="button"
                      ref={bindFirst('discard')}
                      className={`${styles.action} ${styles.dangerTint}`}
                      aria-disabled={busy || undefined}
                      onClick={() => startDiscard(row)}
                    >
                      {t('testimonyDraft.discardDraft')}
                    </button>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </section>
    {statusRegion}
    </>
  );
}

function confirmKeepId(prayerId: string): string {
  return `testimony-draft-keep-${prayerId}`;
}

function confirmId(prayerId: string): string {
  return `testimony-draft-confirm-${prayerId}`;
}

/**
 * True while focus is inside this row's open confirmation, or nowhere at all
 * (the document body): WebKit/Safari does not focus a clicked button and
 * drops focus from the previous one, so a mouse tap on the confirmation's
 * Discard leaves focus on the body. Moving focus then takes it from nothing;
 * focus on any other element is never moved.
 */
function confirmHasFocus(prayerId: string): boolean {
  const active = document.activeElement;
  if (!active || active === document.body) return true;
  const container = document.getElementById(confirmId(prayerId));
  return Boolean(container && container.contains(active));
}

/** Last focus target when the list is gone: the account section heading. */
function focusAccountHeading(): void {
  const heading = document.getElementById('settings-account-title');
  if (!heading) return;
  const added = !heading.hasAttribute('tabindex');
  if (added) heading.setAttribute('tabindex', '-1');
  heading.focus();
  if (added) heading.addEventListener('blur', () => heading.removeAttribute('tabindex'), { once: true });
}
